import { type Registry as Services, service } from '@ember/service';
import Model, { attr, belongsTo } from '@ember-data/model';
import { Type } from '@warp-drive/core-types/symbols';
import type { DayjsTransformReturn } from 'frontend-utils/transforms/dayjs';
import {
  CURRENCY_MAPPING,
  type SupportedCurrencies,
} from 'frontend-utils/utils/constants/currency-mapping-constants';
import type { Day } from 'lumen/models';

interface LosRestriction {
  los: number;
  value: number;
}

// Note: AlternativeLodingSummary id examples:
// 0,2020-05-11,latest
// 0,2020-05-19,2020-05-04
// <type>,<day>,<extract date>
export default class Alternativelodgingsummary extends Model {
  [Type] = 'alternativelodgingsummary' as const;

  @belongsTo('day', { async: false, inverse: 'alternativeLodgingSummaries' })
  declare day: Day;

  @attr('number') declare availability: number;
  @attr('number') declare availabilityBenchmark: number;
  @attr('number') declare averagePrice: number;
  @attr('string') declare averagePriceCurrency: SupportedCurrencies;
  @attr('string') declare benchmarkMessage: string;
  @attr('dayjs') declare extractDate: NonNullable<DayjsTransformReturn>;
  @attr('boolean') declare isLatest: boolean;
  @attr() declare losRestrictions: LosRestriction[];
  @attr('string') declare message: string;
  // Total number of "hotel-like" alt lodging properties we have
  @attr('number') declare numHotelLikeProperties: number;
  // Total number of alt lodging properties we have
  @attr('number') declare numProperties: number;
  @attr('number') declare type: number;

  @service declare intl: Services['intl'];

  get availabilityBenchmarkPercentageNumber(): number | null {
    if (!this.isBenchmarkMissing) {
      return Math.round(this.availabilityBenchmark * 100);
    }

    return null;
  }

  get formattedAvailabiltyPercentageNumber(): number | null {
    if (!this.isSummaryMissing) {
      return Math.round(this.availability * 100);
    }

    return null;
  }

  get formattedAveragePrice(): string | null {
    if (this.isSummaryMissing) {
      return null;
    }

    if (this.isSoldOut) {
      return '--';
    }

    const formattedCurrency =
      CURRENCY_MAPPING && CURRENCY_MAPPING[this.averagePriceCurrency]
        ? CURRENCY_MAPPING[this.averagePriceCurrency]
        : this.averagePriceCurrency;

    return `${formattedCurrency} ${Math.round(this.averagePrice)}`;
  }

  // Formatted value of the unavailableHomesPercentageNumber property. This will be used as metric value in the unvailable homes card
  get formattedUnavailableHomesPercentageNumberMetricValue(): string {
    if (!this.isSummaryMissing) {
      return `${this.unavailableHomesPercentageNumber}%`;
    }

    return this.intl.t('general.no_data');
  }

  get hasData(): boolean {
    return !this.isSummaryMissing;
  }

  get isBenchmarkMissing(): boolean {
    return this.benchmarkMessage === 'general.missing';
  }

  // Specific message for alternativelodging summary. Happens when there is no LOS1 availability (we don't have an average price in that case)
  get isSoldOut(): boolean {
    return this.message === 'alternativelodging.soldout';
  }

  get isSummaryIncomplete(): boolean {
    return this.message === 'search_summary.incomplete_week';
  }

  get isSummaryMissing(): boolean {
    return this.message === 'general.missing';
  }

  get soldOutPercentageNumber(): number | null {
    if (this.isSummaryMissing) {
      return null;
    }

    // We need to calculate the sold out value ourselves
    let soldoutPercentage = 1 - this.availability;

    if (this.losRestrictions) {
      this.losRestrictions.forEach((losRestriction) => {
        soldoutPercentage -= losRestriction.value;
      });
    }

    return Math.round(soldoutPercentage * 100);
  }

  // We receive the availability benchmark % from the API, but the cards show "unavailable", so we need to calculate the shown benchmark value ourselves
  get unavailableHomesBenchmarkPercentageNumber(): number | null {
    if (Number.isFinite(this.availabilityBenchmarkPercentageNumber)) {
      return Math.round((1 - this.availabilityBenchmark) * 100);
    }

    return null;
  }

  // Value we show in the unavailable hotels card bar indicator should be the unavailable + all LOS restrictions (so basically everything that's not available)
  get unavailableHomesPercentageNumber(): number | null {
    if (!this.isSummaryMissing) {
      return Math.round((1 - this.availability) * 100);
    }

    return null;
  }
}
